import React, { useRef, useEffect, useCallback } from 'react';
import { geoOrthographic, geoPath, geoCentroid } from 'd3-geo';
import { select, transition, drag, interpolate } from 'd3';
import countries from './countries.geo.json';
import './style.css';

const Globe = ({ visitedCountries, countryColorMap = {}, onHoverCountry, onSelectCountry }) => {
  const globeRef = useRef(null);
  const width = 500;
  const height = 500;

  const rotateGlobe = useCallback((p, projection, svg, path) => {
    transition()
      .duration(1000)
      .tween('rotate', () => {
        const interpolator = interpolate(projection.rotate(), [-p[0], -p[1]]);
        return (t) => {
          projection.rotate(interpolator(t));
          svg.selectAll('path').attr('d', path);
        };
      });
  }, []);

  const focusOnCountry = useCallback((id, projection, svg, path) => {
    const country = countries.features.find(c => c.id === id);
    if (country) {
      const centroid = geoCentroid(country);
      rotateGlobe(centroid, projection, svg, path);
    }
  }, [rotateGlobe]);

  useEffect(() => {
    const svg = select(globeRef.current);
    const projection = geoOrthographic().scale(250).translate([width / 2, height / 2]);
    const path = geoPath().projection(projection);

    // Draw the globe (water)
    svg.append('path')
      .datum({ type: 'Sphere' })
      .attr('class', 'water')
      .attr('d', path);

    // Draw countries
    svg.selectAll('path.land')
      .data(countries.features)
      .enter()
      .append('path')
      .attr('class', 'land')
      .attr('d', path)
      .each(function (d) {
        const countryElement = select(this);
        const isVisited = visitedCountries.some(country => country.id === d.id);

        if (isVisited) {
          const color = countryColorMap[d.id];

          countryElement
            .attr('class', 'visited')
            .style('fill', color || null)
            .on('mouseenter', () => {
              countryElement.style('fill', color ? darkenColor(color) : null);
              onHoverCountry(d);
              onSelectCountry(d);
            })
            .on('mouseleave', () => {
              countryElement.style('fill', color || null);
              onHoverCountry(null);
            })
            .on('click', () => {
              focusOnCountry(d.id, projection, svg, path);
              onSelectCountry(d);
            });
        }
      });

    // Drag functionality
    svg.call(
      drag().on('drag', (event) => {
        const rotate = projection.rotate();
        const k = 75 / projection.scale();
        projection.rotate([rotate[0] + event.dx * k, rotate[1] - event.dy * k]);
        svg.selectAll('path').attr('d', path);
      })
    );
  }, [visitedCountries, countryColorMap, focusOnCountry, onHoverCountry, onSelectCountry]);

  return <svg id="globe" ref={globeRef} viewBox={`0 0 ${width} ${height}`} />;
};

const darkenColor = (hex) => {
  const amount = -30;
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

export default Globe;